import React, { Component } from 'react';

import { AsyncSelect } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import { UserOption } from '@qality/common-model/src/UserOption';
import { UserService } from '@qality/common-service';
import qalityIssueIcon from '@qality/common-static/images/qality-issue-icon.png';
import { Utils } from '@qality/common-utils';

import { UserPickerOption } from './UserPickerOption';
import { UserPickerValue } from './UserPickerValue';

const UNASSIGNED = {
  label: 'Unassigned',
  value: null,
};

const REMOVED_USER = {
  label: 'Removed user',
  value: 'removed_user',
};

export const AUTOMATED_EXECUTION_USER = {
  label: 'Automated test import',
  value: 'automated_execution_user',
  avatar: qalityIssueIcon,
  user: undefined,
};

type Props = {
  projectKey: string;
  onChange: (newExecutionAssignee: UserOption) => void;
  isDisabled: boolean;
  tooltip?: string;
  userId?: string;
  defaultUserPickerOptions?: Promise<Array<UserOption>>;
  selectedValue?: UserOption;
  menuPlacement?: 'bottom' | 'auto' | 'top';
  isLoadingAssignees?: boolean;
};

type State = {
  selectValues: Array<UserOption>;
  selectValue: UserOption;
};

export default class UserPicker extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectValues: [],
      selectValue: UNASSIGNED,
    };
  }

  async componentDidMount(): Promise<void> {
    let users = await this.initOptions();
    users = await this.joinSelectedUser(users);

    this.setState({
      selectValues: users,
    });
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.props.selectedValue !== prevProps.selectedValue) {
      this.setState({
        selectValue: this.props.selectedValue ?? UNASSIGNED,
      });
    }
  }

  // eslint-disable-next-line complexity
  joinSelectedUser = async (
    users: Array<UserOption>
  ): Promise<UserOption[]> => {
    const { userId } = this.props;
    if (!Utils.isNilOrEmpty(userId) && userId !== UNASSIGNED.value) {
      let user = users.find((userItem) => userItem.value === userId);
      if (!user) {
        if (this.props.selectedValue) {
          user = this.props.selectedValue;
        } else {
          user = await UserService.retrieveUserSelectObjectById(userId);
        }
        users.push(user);
      }
      if (user) {
        this.setState({
          selectValue: user,
        });
      }
    }
    return users;
  };

  initOptions = async (): Promise<UserOption[]> => {
    const { userId } = this.props;
    const users = this.props.defaultUserPickerOptions
      ? [...(await this.props.defaultUserPickerOptions)]
      : await this.loadOptions();

    if (userId == AUTOMATED_EXECUTION_USER.value) {
      users.unshift(AUTOMATED_EXECUTION_USER);
    }

    if (userId == REMOVED_USER.value) {
      users.unshift(REMOVED_USER);
    }

    users.unshift(UNASSIGNED);
    return users;
  };

  loadOptions = async (inputValue?: string): Promise<UserOption[]> => {
    const response = await UserService.findUsersAssignableToProject(
      this.props.projectKey,
      inputValue
    );
    const users = response.map(function (user) {
      return UserService.createSelectUserObject(user);
    });
    return users;
  };

  onChange = async (option: UserOption): Promise<void> => {
    const useFallbackValue = Utils.isNilOrEmpty(option);
    const selectedOption = useFallbackValue ? UNASSIGNED : option;

    const isSelectedValueOnList = this.state.selectValues.some(
      (userItem) => userItem.value === selectedOption.value
    );

    let selectValues = isSelectedValueOnList
      ? [...this.state.selectValues]
      : [...this.state.selectValues, selectedOption];
    // we don't want user to be able to reselect this value.
    selectValues = selectValues.filter(
      (userItem) => userItem.value !== AUTOMATED_EXECUTION_USER.value
    );

    this.setState({
      selectValue: selectedOption,
      selectValues,
    });
    this.props.onChange(selectedOption);
  };

  render(): JSX.Element {
    return (
      <div className='hide-focus-wrapper'>
        <Tooltip content={this.props.tooltip}>
          <div>
            <AsyncSelect
              id='user-picker'
              isDisabled={
                this.props.isDisabled || this.props.isLoadingAssignees
              }
              defaultOptions={this.state.selectValues}
              loadOptions={this.loadOptions}
              placeholder='Search user...'
              onChange={this.onChange}
              isClearable={true}
              value={this.state.selectValue}
              components={{
                Option: UserPickerOption,
                SingleValue: UserPickerValue,
              }}
              menuPlacement={this.props.menuPlacement}
              isLoading={this.props.isLoadingAssignees}
              spacing='compact'
            />
          </div>
        </Tooltip>
      </div>
    );
  }
}
